import { isAxiosError } from "helpers/utils.helper"
import { sanitizeFileNamePart, stripPdfExtension } from "helpers/fileName.helper"
import { RESULT_PDF_MESSAGES, RESULT_PDF_NAME_PART_MAX_LENGTH } from "helpers/resultPdf.constants"

const sanitizeNamePart = (value: string) => sanitizeFileNamePart(value, RESULT_PDF_NAME_PART_MAX_LENGTH)

// Thrown when the build endpoint itself reports `generation_status: "failed"` — distinct from an
// axios/network error, so `getResultPdfErrorMessage` can tell "the request failed" apart from "the
// request succeeded, but the build it started did not."
export class ResultPdfGenerationFailedError extends Error {}

// Thrown when polling exceeds RESULT_PDF_POLL_TIMEOUT_MS without the job reaching a terminal state.
// The CSV flow has no equivalent: it is a single synchronous call with its own axios timeout instead.
export class ResultPdfPollTimeoutError extends Error {}

// Composes the download name for the on-demand annotated result PDF (#1401):
// `<original PDF>__quotation_<quotation name>.pdf`, matching the CSV convention (REQ-420) and the
// name the backend documents for this endpoint's own presigned link.
//
// Includes the `.pdf` extension, matching `buildResultCsvFileName` — `downloadFileService.downloadFromUrl`
// (the presigned-URL download path this flow uses) takes the whole file name as-is, extension
// included, same as `downloadBlob`.
//
// `quotationName` is required, with no unnamed case to handle: the API never returns a null name —
// `Quotations.to_dict()` substitutes "Untitled" — so a quotation always has one from here.
//
// `drawingName` is genuinely optional: its query can still be in flight on the first paint after the
// drawer opens, or have failed. Permissions are not a factor — the drawing endpoint carries the same
// `IsOwner | IsSuperAdminOrAdmin` as the PDF endpoints, so anyone offered this control can read it.
export const buildResultPdfFileName = ({
	quotationName,
	drawingName,
}: {
	quotationName: string
	drawingName?: string | null
}): string => {
	const drawingPart = drawingName ? sanitizeNamePart(stripPdfExtension(drawingName)) : ""
	const quotationPart = sanitizeNamePart(quotationName)

	return drawingPart
		? `${drawingPart}__quotation_${quotationPart}.pdf`
		: `quotation_${quotationPart}.pdf`
}

// Maps a failure of the on-demand PDF flow to short, actionable copy. Three source shapes, checked in
// order: our own poll timeout, the build endpoint's own terminal "failed" status, then an axios error
// from either the start or the poll request (401/403/404/409 + client timeout, same mapping as the
// CSV flow's `getResultCsvErrorMessage`).
export const getResultPdfErrorMessage = (error: unknown): string => {
	if (error instanceof ResultPdfPollTimeoutError) return RESULT_PDF_MESSAGES.timedOut
	if (error instanceof ResultPdfGenerationFailedError) return RESULT_PDF_MESSAGES.failed
	if (!isAxiosError(error)) return RESULT_PDF_MESSAGES.failed
	// Axios reports both its own client timeout and a dropped connection as ECONNABORTED.
	if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return RESULT_PDF_MESSAGES.timedOut

	switch (error.response?.status) {
		// There is no global 401 interceptor, so this has to say "signed out" itself — otherwise an
		// expired token reads as a generation failure and invites an endless retry.
		case 401:
			return RESULT_PDF_MESSAGES.unauthorized
		case 403:
			return RESULT_PDF_MESSAGES.forbidden
		case 404:
			return RESULT_PDF_MESSAGES.notFound
		case 409:
			return RESULT_PDF_MESSAGES.submitted
		default:
			return RESULT_PDF_MESSAGES.failed
	}
}
